// ============================================================
// SUPABASE GUARD — Circuit breaker for RLS errors & race conditions
// ============================================================
// This module provides safe wrappers around Supabase queries:
// 1. Blocks queries when there is no active session (race condition guard)
// 2. Catches RLS 403/42501 errors and returns empty data (circuit breaker)
// 3. Auto-retries once after waiting for token refresh (handles refresh races)
// 4. On persistent auth corruption, performs a hard logout
// ============================================================

import { supabase } from './supabaseClient';

// --- Time-windowed failure tracking ---
// Instead of a simple counter, we track timestamps of failures.
// Only failures within the WINDOW_MS count toward the threshold.
const WINDOW_MS = 30_000;        // 30-second sliding window        // 15-second sliding window
const MAX_FAILURES_IN_WINDOW = 10; // Need 10 RLS failures in 30s to trigger logout // Need 6 RLS failures in 15s to trigger logout
let failureTimestamps = [];

function recordFailure() {
    const now = Date.now();
    failureTimestamps.push(now);
    // Prune old entries outside the window
    failureTimestamps = failureTimestamps.filter(ts => now - ts < WINDOW_MS);
    return failureTimestamps.length;
}

function isCircuitTripped() {
    const now = Date.now();
    failureTimestamps = failureTimestamps.filter(ts => now - ts < WINDOW_MS);
    return failureTimestamps.length >= MAX_FAILURES_IN_WINDOW;
}

/**
 * Check if an error is an RLS/permission error.
 * Supabase returns these as:
 * - HTTP 403
 * - PostgreSQL code '42501' (insufficient_privilege)
 * - PGRST code 'PGRST116' (schema cache / permission)
 */
export function isRLSError(error) {
    if (!error) return false;
    const msg = (error.message || '').toLowerCase();
    const code = (error.code || '').toString();
    const hint = (error.hint || '').toLowerCase();

    return (
        code === '42501' ||
        code === 'PGRST116' ||
        msg.includes('permission denied') ||
        msg.includes('row-level security') ||
        msg.includes('new row violates row-level security') ||
        msg.includes('violates row-level security policy') ||
        (error.status === 403) ||
        hint.includes('rls')
    );
}

/**
 * Check if an error is a transient network/fetch error.
 */
function isTransientError(error) {
    if (!error) return false;
    const msg = (error.message || '').toLowerCase();
    return (
        msg.includes('failed to fetch') ||
        msg.includes('load failed') ||
        msg.includes('networkerror') ||
        msg.includes('aborterror') ||
        msg.includes('the operation was aborted') ||
        msg.includes('timeout') ||
        msg.includes('network request failed') ||
        error.name === 'AbortError' ||
        error.name === 'TypeError' // fetch failures manifest as TypeError
    );
}

/**
 * Hard logout — clears all session state and redirects to login.
 * Used as a last resort when the auth token is irrecoverably corrupted.
 * Now protected against being called multiple times.
 */
/**
 * NOTE: hardLogout has been intentionally removed.
 * Session termination is ONLY handled by AuthContext.jsx (via supabase.auth.signOut).
 * This prevents any query failure or React error from accidentally destroying
 * a valid user session. If you need to log out, use the signOut() from useAuth().
 */

/**
 * Wait for the Supabase client to finish refreshing the token.
 * Returns the new session or null.
 */
async function waitForTokenRefresh() {
    try {
        // Give the auto-refresh up to 3 seconds to complete
        const result = await Promise.race([
            supabase.auth.getSession(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('refresh_timeout')), 3000))
        ]);
        return result?.data?.session || null;
    } catch {
        return null;
    }
}

/**
 * Safe query wrapper — guards against race conditions and RLS errors.
 * 
 * Key improvements over previous version:
 * - Time-windowed failure tracking (not raw counter)
 * - Auto-retry once after token refresh for RLS errors
 * - Graceful handling of transient network errors (no logout)
 * - AbortError / TypeError are silently swallowed (component unmount)
 * 
 * Usage:
 *   const { data, error } = await safeQuery(
 *     () => supabase.from('notifications').select('*').eq('user_id', userId),
 *     { fallback: [], context: 'NotificationsBell' }
 *   );
 * 
 * @param {Function} queryFn - A function that returns a Supabase query builder (thenable)
 * @param {Object} options
 * @param {*} options.fallback - Value to return as data if query fails (default: [])
 * @param {string} options.context - Label for debugging (e.g. component name)
 * @returns {{ data: *, error: Error|null }}
 */
export async function safeQuery(queryFn, options = {}) {
    const { fallback = [], context = 'unknown' } = options;

    try {
        const result = await queryFn();
        const { data, error } = result;

        if (error) {
            // --- Transient / Network errors ---
            // These should NEVER trigger logout. The user might just have
            // flaky wifi or the tab was backgrounded.
            if (isTransientError(error)) {
                console.warn(`[GUARD:${context}] Transient error — returning fallback`, error.message);
                return { data: fallback, error };
            }

            // --- RLS errors ---
            if (isRLSError(error)) {
                console.warn(`[GUARD:${context}] RLS error — attempting token refresh retry`, error.message);

                // RETRY ONCE: wait for token refresh, then re-execute
                const session = await waitForTokenRefresh();
                if (session) {
                    try {
                        const retryResult = await queryFn();
                        if (!retryResult.error) {
                            // Retry succeeded — the token was just stale
                            return { data: retryResult.data, error: null };
                        }
                    } catch {
                        // Retry also failed, fall through to failure tracking
                    }
                }

                // Still failing after retry — record and check circuit
                const failCount = recordFailure();
                console.error(`[GUARD:${context}] RLS error persists after retry (${failCount}/${MAX_FAILURES_IN_WINDOW} in window)`);

                // REMOVED hardLogout here. We should not log the user out 
                // just because a query failed RLS or returned 403.
                // It causes "random" session drops if the server has issues or WAF blocks a request.

                return { data: fallback, error };
            }

            // --- Other Supabase errors (not RLS, not transient) ---
            // Return them normally, don't count as auth failures
            return { data, error };
        }

        // Success — no action needed (failures auto-expire from time window)
        return { data, error: null };

    } catch (err) {
        // --- JS-level exceptions ---
        // AbortError = component unmounted, perfectly normal
        if (err?.name === 'AbortError' || (err?.message || '').includes('aborted')) {
            return { data: fallback, error: null }; // Swallow silently
        }

        // Transient network errors at the fetch level
        if (isTransientError(err)) {
            console.warn(`[GUARD:${context}] Network error (caught) — returning fallback`);
            return { data: fallback, error: err };
        }

        console.error(`[GUARD:${context}] Unexpected error`, err);
        return { data: fallback, error: err };
    }
}

/**
 * Reset the auth failure window.
 * Call this after a successful login to clear any stale failure records.
 */
export function resetAuthFailures() {
    failureTimestamps = [];
}
